(() => {
  'use strict';
  // Scalloped borders (kids-border + scalloped-border products, marseladesign.com 2026-10-25, ported from the
  // Türkiye store). The customer enters the wall width and the border height from the floor (the paper height:
  // the scalloped band is its top edge, the stripes run down to the baseboard), cm or in, and a material; the
  // quote worker prices and freezes the selection (USD, or EUR for the Europe market) and this script adds exactly
  // that variant to the cart. Shared logic: assets/kind-core.js. Must match BORDERS / BORDER_GEOMETRY in the worker
  // (com-tasima/desen-bordur/arac/add_kinds_com.py) and snippets/border-registry.liquid.
  const K = globalThis.MarselaKinds;
  if (!K) return;
  const {KindError, finite, fill, MATERIALS, MAX_WIDTH_CM} = K;
  const BORDER = Object.freeze({label: 'Scalloped border', tileWidthCm: 50, tileHeightCm: 100, bandHeightCm: 12.1375, stripeWidthCm: 2.5, anchor: 'top', origin: 'top-left'});
  const MIN_HEIGHT_CM = 20, MAX_HEIGHT_CM = 420;
  // Preview only: the painted wall above the border, and a crib (135 × 90 cm) for scale.
  const ROOM_HEIGHT_CM = 260;
  const REFERENCES = Object.freeze({crib: {widthCm: 135, heightCm: 90, right: 0.06}});
  const EXAMPLE = Object.freeze({widthCm: 300, heightCm: 110});
  const FORM_TEXT = Object.freeze({
    example: 'Example: 300 cm wide, border 110 cm from the floor. Enter your own size.',
    caption: 'Your wall: {width} wide · scalloped border {height} from the floor · ceiling shown at {room}',
    selection: 'Please enter the wall width and the border height first.'
  });

  function borderFrom(data) {
    const border = {handle: data?.borderHandle, reference: data?.borderReference, sha256: data?.borderSha256, asset: data?.borderAsset, sourceWidth: Number(data?.borderWidth), sourceHeight: Number(data?.borderHeight), src: data?.borderSrc, ground: /^#[0-9a-fA-F]{6}$/.test(data?.borderGround || '') ? data.borderGround : '#f4eee0'};
    if (typeof border.handle !== 'string' || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(border.handle) || !/^MWC18\d{2}$/.test(border.reference || '') || !border.handle.endsWith(`-${border.reference.toLowerCase()}`)) return null;
    if (!/^[a-f0-9]{64}$/.test(border.sha256 || '') || border.asset !== `customizer-border-${border.reference.toLowerCase()}.png` || border.sourceWidth !== 1000 || border.sourceHeight !== 2000 || typeof border.src !== 'string' || !border.src) return null;
    return Object.freeze(border);
  }
  function canonicalConfiguration(input, border) {
    if (!border || input?.schemaVersion !== 1 || input.kind !== 'border' || input.productHandle !== border.handle || input.artwork?.reference !== border.reference || input.artwork?.sha256 !== border.sha256) throw new KindError('selection');
    const wall = input.wall;
    if (!wall || !finite(wall.widthCm) || !finite(wall.heightCm) || wall.widthCm <= 0 || wall.widthCm > MAX_WIDTH_CM || wall.heightCm < MIN_HEIGHT_CM || wall.heightCm > MAX_HEIGHT_CM) throw new KindError('selection');
    if (typeof input.material !== 'string' || !Object.hasOwn(MATERIALS, input.material)) throw new KindError('selection');
    return {schemaVersion: 1, kind: 'border', productHandle: border.handle, artwork: {reference: border.reference, sha256: border.sha256}, wall: {widthCm: wall.widthCm, heightCm: wall.heightCm}, material: input.material};
  }
  function validateQuote(data, configuration, border, endpoint, ready, currency = 'USD') {
    const c = data?.configuration;
    let returned;
    try { returned = canonicalConfiguration({schemaVersion: c?.schemaVersion, kind: c?.kind, productHandle: c?.productHandle, artwork: {reference: c?.artwork?.reference, sha256: c?.artwork?.sha256}, wall: c?.wall, material: c?.material?.id}, border); } catch (_) { throw new KindError('quote'); }
    if (JSON.stringify(returned) !== JSON.stringify(configuration) || JSON.stringify(c.border) !== JSON.stringify(BORDER)) throw new KindError('quote');
    return K.validateCommon(data, configuration, border, endpoint, ready, currency, 'vectorSha256');
  }
  function properties(quoteData) {
    const c = quoteData.configuration;
    return K.baseProperties(quoteData, {'Wall width': `${K.plain(c.wall.widthCm)} cm`, 'Border height': `${K.plain(c.wall.heightCm)} cm`});
  }
  // Exercised by com-tasima/desen-bordur/arac/test_kinds_contract.mjs.
  if (typeof module === 'object' && module.exports) module.exports = {BORDER, borderFrom, canonicalConfiguration, validateQuote, properties};
  if (typeof document === 'undefined') return;

  const sized = (src, width) => { try { const url = new URL(src, location.href); url.searchParams.set('width', String(width)); return url.href; } catch (_) { return src; } };
  const lighten = (hex, amount) => { const n = parseInt(hex.slice(1), 16), mix = v => Math.round(v + (255 - v) * amount); return `rgb(${mix(n >> 16)}, ${mix((n >> 8) & 255)}, ${mix(n & 255)})`; };

  function mount(form) {
    if (form.dataset.msBorderReady) return;
    const border = borderFrom(form.dataset);
    if (!border) return;
    form.dataset.msBorderReady = 'true';
    const $ = selector => form.querySelector(selector), $$ = selector => [...form.querySelectorAll(selector)];
    const width = $('[data-ms-border-width]'), height = $('[data-ms-border-height]'), error = $('[data-ms-border-error]');
    const materials = $$('[data-ms-border-material]');
    const stage = $('[data-ms-border-stage]'), wallBox = $('[data-ms-border-wall]'), canvas = $('[data-ms-border-canvas]'), caption = $('[data-ms-border-caption]');
    const produced = $('[data-ms-border-produced]'), panels = $('[data-ms-border-panels]'), area = $('[data-ms-border-area]'), total = $('[data-ms-border-total]'), priceLabel = $('[data-ms-border-price-label]');
    const button = $('[data-ms-border-add]'), buttonLabel = $('[data-ms-border-add-label]'), status = $('[data-ms-border-status]'), viewCart = $('[data-ms-border-cart]');
    const crib = $('[data-ms-border-ref="crib"]');
    if (!width || !height || !canvas || !wallBox || !button || !status) return;
    const page = K.pageContext(), fmt = K.formatter(page.locale), TEXT = K.textFor(form, FORM_TEXT, page.eurEnabled);
    const client = K.createClient({endpoint: page.endpoint, root: page.root});
    const enabled = page.enabled && Boolean(client.service);
    const store = K.storage(`marsela-border-v1:${border.handle}:${K.RATE_VERSION}`);
    const paint = lighten(border.ground, 0.35);
    viewCart.href = client.route('cart');
    const units = K.bindUnit(form, [width, height], () => changed());

    const image = new Image();
    image.decoding = 'async';
    image.addEventListener('load', () => schedule());
    image.src = sized(border.src, 1000);

    let phase = 'idle', errorCode = null, busy = false, slowTimer = 0, slow = false;
    const chosen = list => list.find(input => input.checked)?.value;
    function readWall() {
      const w = units.read(width), h = units.read(height);
      const widthBad = width.value.trim() !== '' && (w === null || w > MAX_WIDTH_CM);
      const heightBad = height.value.trim() !== '' && (h === null || h < MIN_HEIGHT_CM || h > MAX_HEIGHT_CM);
      const valid = w !== null && h !== null && w <= MAX_WIDTH_CM && h >= MIN_HEIGHT_CM && h <= MAX_HEIGHT_CM;
      return {valid, wall: valid ? {widthCm: w, heightCm: h} : null, invalid: widthBad || heightBad};
    }
    function currentConfiguration() {
      const state = readWall();
      if (!state.valid) return null;
      try { return canonicalConfiguration({schemaVersion: 1, kind: 'border', productHandle: border.handle, artwork: {reference: border.reference, sha256: border.sha256}, wall: state.wall, material: chosen(materials)}, border); } catch (_) { return null; }
    }
    function draw() {
      const state = readWall(), wall = state.wall || EXAMPLE;
      const roomHeight = Math.max(ROOM_HEIGHT_CM, wall.heightCm);
      const maxWidth = stage.clientWidth || 320, maxHeight = Math.min(360, Math.max(180, window.innerHeight * 0.42));
      let boxWidth = maxWidth, boxHeight = maxWidth * roomHeight / wall.widthCm;
      if (boxHeight > maxHeight) { boxHeight = maxHeight; boxWidth = maxHeight * wall.widthCm / roomHeight; }
      wallBox.style.width = `${Math.max(24, boxWidth)}px`; wallBox.style.height = `${Math.max(24, boxHeight)}px`;
      const ratio = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = Math.max(1, Math.round(boxWidth * ratio)); canvas.height = Math.max(1, Math.round(boxHeight * ratio));
      const ctx = canvas.getContext('2d'), perCm = canvas.height / roomHeight, top = canvas.height - wall.heightCm * perCm;
      ctx.fillStyle = paint; ctx.fillRect(0, 0, canvas.width, canvas.height);
      if (image.complete && image.naturalWidth) {
        // The tile is 50 × 100 cm with the band at its top: the top half goes at the top of the
        // paper, the lower half (stripes only) repeats down to the floor.
        const tileWidth = BORDER.tileWidthCm * perCm, half = BORDER.tileHeightCm / 2 * perCm, sw = image.naturalWidth, sh = image.naturalHeight / 2;
        ctx.save(); ctx.beginPath(); ctx.rect(0, top, canvas.width, canvas.height - top); ctx.clip();
        for (let y = top + half; y < canvas.height; y += half) for (let x = 0; x < canvas.width; x += tileWidth) ctx.drawImage(image, 0, sh, sw, sh, x, y, tileWidth + 0.5, half + 0.5);
        for (let x = 0; x < canvas.width; x += tileWidth) ctx.drawImage(image, 0, 0, sw, sh, x, top, tileWidth + 0.5, half + 0.5);
        ctx.restore();
      }
      // Panel joins (at most 104 cm wide each) across the wallpaper only.
      const plan = K.panelPlan(wall);
      ctx.save(); ctx.strokeStyle = 'rgba(38,62,53,.42)'; ctx.lineWidth = ratio; ctx.setLineDash([5 * ratio, 4 * ratio]);
      for (let index = 1; index < plan.count; index++) { const x = Math.round(canvas.width * index / plan.count) + 0.5; ctx.beginPath(); ctx.moveTo(x, top); ctx.lineTo(x, canvas.height); ctx.stroke(); }
      ctx.restore();
      if (crib) {
        crib.style.width = `${Math.min(100, REFERENCES.crib.widthCm / wall.widthCm * 100)}%`;
        crib.style.height = `${Math.min(100, REFERENCES.crib.heightCm / roomHeight * 100)}%`;
        crib.style.right = `${REFERENCES.crib.right * 100}%`;
      }
      const unit = units.unit;
      caption.textContent = state.wall ? fill(TEXT.caption, {width: fmt.length(wall.widthCm, unit), height: fmt.length(wall.heightCm, unit), room: fmt.length(roomHeight, unit)}) : TEXT.example;
    }
    let frame = 0;
    function schedule() { if (!frame) frame = requestAnimationFrame(() => { frame = 0; draw(); }); }
    function render() {
      const state = readWall(), configuration = currentConfiguration();
      error.hidden = !state.invalid;
      if (configuration) {
        const plan = K.panelPlan(configuration.wall), m2 = configuration.wall.widthCm * configuration.wall.heightCm / 10000, areaText = `${fmt.number(m2, 2)} m²`;
        produced.textContent = fmt.size(configuration.wall, units.unit);
        panels.textContent = `${plan.count} × ${fmt.length(plan.panelWidthCm, units.unit)}`;
        area.textContent = m2 < K.MIN_BILLABLE_M2 ? fill(TEXT.pricedAs, {area: areaText}) : areaText;
        total.textContent = fmt.money(K.expectedPrice(configuration, page.displayCurrency), page.displayCurrency);
      } else { produced.textContent = '—'; panels.textContent = '—'; area.textContent = '—'; total.textContent = '—'; }
      if (priceLabel) priceLabel.textContent = phase === 'added' ? TEXT.price : TEXT.estimate;
      buttonLabel.textContent = phase === 'preparing' ? TEXT.preparing : phase === 'adding' || phase === 'added' ? TEXT.adding : phase === 'uncertain' ? TEXT.view : phase === 'failed' ? TEXT.retry : TEXT.confirmAdd;
      button.disabled = busy || !enabled;
      button.setAttribute('aria-busy', busy ? 'true' : 'false');
      status.textContent = !enabled ? TEXT.unavailable : errorCode ? (TEXT[errorCode] || TEXT.network) : phase === 'preparing' ? (slow ? TEXT.slow : TEXT.preparingNote) : phase === 'adding' ? TEXT.adding : phase === 'added' ? TEXT.added : TEXT.idle;
      viewCart.hidden = !(phase === 'uncertain' || phase === 'added');
      schedule();
    }
    function changed() {
      if (busy) return;
      if (phase !== 'idle' && phase !== 'uncertain') { phase = 'idle'; errorCode = null; }
      render();
    }
    [width, height].forEach(field => {
      field.addEventListener('input', changed);
      field.addEventListener('keydown', event => { if (event.key === 'Enter') { event.preventDefault(); field.blur(); } });
    });
    materials.forEach(input => input.addEventListener('change', changed));
    form.addEventListener('submit', event => event.preventDefault());
    canvas.addEventListener('contextmenu', event => event.preventDefault());

    button.addEventListener('click', async () => {
      if (phase === 'uncertain') { location.assign(client.route('cart')); return; }
      if (busy || !enabled) return;
      const configuration = currentConfiguration();
      if (!configuration) {
        errorCode = 'selection'; phase = 'failed'; render();
        (units.read(width) === null ? width : height).focus();
        return;
      }
      busy = true; phase = 'preparing'; errorCode = null; slow = false; render();
      const controller = new AbortController(), timeout = setTimeout(() => controller.abort(), 75000);
      slowTimer = setTimeout(() => { slow = true; render(); }, 25000);
      try {
        const current = await client.cart(controller.signal), currency = current.currency;
        if (!K.currencyAllowed(currency, page.eurEnabled)) throw new KindError('currency');
        const key = K.reuseKey(store.read(value => canonicalConfiguration(value, border)), configuration, currency);
        store.write({configuration, idempotencyKey: key, currency, unit: units.unit});
        const ready = await client.quote(configuration, currency, key, controller.signal, (data, isReady) => validateQuote(data, configuration, border, client.service, isReady, currency));
        clearTimeout(slowTimer); phase = 'adding'; render();
        await client.add(ready, currency, properties(ready), controller.signal);
        phase = 'added'; render();
        location.assign(client.route('cart'));
      } catch (failure) {
        const code = failure?.name === 'AbortError' || failure?.code === 'cancelled' ? (phase === 'adding' ? 'uncertain' : 'pending') : typeof failure?.code === 'string' && Object.hasOwn(TEXT, failure.code) ? failure.code : 'network';
        errorCode = code; phase = code === 'uncertain' ? 'uncertain' : 'failed';
      } finally { clearTimeout(timeout); clearTimeout(slowTimer); busy = false; slow = false; render(); }
    });

    const stored = store.read(value => canonicalConfiguration(value, border));
    if (stored) {
      units.set(stored.unit);
      width.value = units.show(stored.configuration.wall.widthCm);
      height.value = units.show(stored.configuration.wall.heightCm);
      materials.forEach(input => { input.checked = input.value === stored.configuration.material; });
    }
    if (typeof ResizeObserver === 'function') new ResizeObserver(schedule).observe(stage);
    else window.addEventListener('resize', schedule);
    render();
  }

  function init(scope = document) { scope.querySelectorAll('[data-ms-border-entry]').forEach(mount); }
  init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
